import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Zap, Calculator } from "lucide-react";
import { Button } from "../../components/ui/button";

const MATERIALS = [
  { key: "cu", label: "Kupfer (Cu)", conductivity: 56 },
  { key: "al", label: "Aluminium (Al)", conductivity: 35 },
];

const CROSS_SECTIONS = [1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120, 150, 185, 240];

const FUSE_RATINGS = [6, 10, 13, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125, 160];

// Max current per cross-section (Cu, single-phase, NYM in installation)
const MAX_CURRENT_CU = {
  1.5: 16, 2.5: 25, 4: 32, 6: 40, 10: 63, 16: 80, 25: 100, 35: 125,
  50: 160, 70: 200, 95: 250, 120: 290, 150: 330, 185: 380, 240: 440,
};

export default function Kabelrechner() {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [voltage, setVoltage] = useState(230);
  const [power, setPower] = useState("");
  const [length, setLength] = useState("");
  const [material, setMaterial] = useState("cu");
  const [maxDropPercent, setMaxDropPercent] = useState(3);
  const [cosFi, setCosFi] = useState(1);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const P = parseFloat(power);
    const L = parseFloat(length);
    if (!P || !L) return;

    const mat = MATERIALS.find(m => m.key === material);
    const I = P / (voltage * cosFi);
    const phases = voltage > 300 ? 3 : 1;
    const factor = phases === 3 ? Math.sqrt(3) : 2;

    // Minimum cross-section for voltage drop
    const minA = (factor * L * I) / (mat.conductivity * (maxDropPercent / 100) * voltage);

    // Find recommended cross-section
    const recommended = CROSS_SECTIONS.find(cs => cs >= minA) || CROSS_SECTIONS[CROSS_SECTIONS.length - 1];

    // Actual voltage drop with recommended cross-section
    const actualDrop = (factor * L * I) / (mat.conductivity * recommended) / voltage * 100;

    // Recommended fuse
    const recommendedFuse = FUSE_RATINGS.find(f => f >= I) || FUSE_RATINGS[FUSE_RATINGS.length - 1];

    // Max current for this cross-section
    const maxI = MAX_CURRENT_CU[recommended] || 0;

    setResult({
      current: I.toFixed(2),
      minCrossSection: minA.toFixed(2),
      recommended,
      actualDrop: actualDrop.toFixed(2),
      recommendedFuse,
      maxCurrent: maxI,
      phases,
    });
  };

  const inputClass = "w-full px-3 py-2 bg-[#1a1f2e] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500";
  const labelClass = "text-xs text-gray-400 font-medium mb-1 block";

  return (
    <div className="min-h-screen bg-[#0f1117] text-white pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f1117]/95 backdrop-blur border-b border-gray-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            <h1 className="text-base font-semibold">Kabelrechner</h1>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Inputs */}
        <div className="bg-[#161b26] rounded-xl p-4 border border-gray-800 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Spannung (V)</label>
              <select value={voltage} onChange={e => setVoltage(Number(e.target.value))} className={inputClass} data-testid="kb-voltage">
                <option value={230}>230V (1-phasig)</option>
                <option value={400}>400V (3-phasig)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Leistung (W)</label>
              <input type="number" value={power} onChange={e => setPower(e.target.value)} placeholder="z.B. 3000" className={inputClass} data-testid="kb-power" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Leitungslaenge (m)</label>
              <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="z.B. 25" className={inputClass} data-testid="kb-length" />
            </div>
            <div>
              <label className={labelClass}>Material</label>
              <select value={material} onChange={e => setMaterial(e.target.value)} className={inputClass} data-testid="kb-material">
                {MATERIALS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Max. Spannungsfall (%)</label>
              <select value={maxDropPercent} onChange={e => setMaxDropPercent(Number(e.target.value))} className={inputClass} data-testid="kb-maxdrop">
                <option value={1}>1%</option>
                <option value={2}>2%</option>
                <option value={3}>3% (Standard)</option>
                <option value={5}>5%</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>cos phi</label>
              <select value={cosFi} onChange={e => setCosFi(Number(e.target.value))} className={inputClass} data-testid="kb-cosfi">
                <option value={1}>1.0 (ohm.)</option>
                <option value={0.95}>0.95</option>
                <option value={0.9}>0.9</option>
                <option value={0.85}>0.85</option>
                <option value={0.8}>0.8</option>
              </select>
            </div>
          </div>

          <Button onClick={calculate} className="w-full bg-blue-600 hover:bg-blue-700 gap-2" data-testid="kb-calculate">
            <Calculator className="w-4 h-4" /> Berechnen
          </Button>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-[#161b26] rounded-xl p-4 border border-gray-800 space-y-3" data-testid="kb-result">
            <h3 className="text-sm font-semibold text-blue-400">Ergebnis</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#1a1f2e] rounded-lg p-3">
                <p className="text-xs text-gray-400">Strom</p>
                <p className="text-lg font-bold text-white">{result.current} A</p>
              </div>
              <div className="bg-[#1a1f2e] rounded-lg p-3">
                <p className="text-xs text-gray-400">Empf. Sicherung</p>
                <p className="text-lg font-bold text-yellow-400">{result.recommendedFuse} A</p>
              </div>
              <div className="bg-[#1a1f2e] rounded-lg p-3">
                <p className="text-xs text-gray-400">Min. Querschnitt</p>
                <p className="text-lg font-bold text-white">{result.minCrossSection} mm2</p>
              </div>
              <div className="bg-emerald-900/30 rounded-lg p-3 border border-emerald-800">
                <p className="text-xs text-emerald-400">Empf. Querschnitt</p>
                <p className="text-lg font-bold text-emerald-300">{result.recommended} mm2</p>
              </div>
              <div className="bg-[#1a1f2e] rounded-lg p-3">
                <p className="text-xs text-gray-400">Spannungsfall</p>
                <p className={`text-lg font-bold ${parseFloat(result.actualDrop) > maxDropPercent ? 'text-red-400' : 'text-green-400'}`}>{result.actualDrop}%</p>
              </div>
              <div className="bg-[#1a1f2e] rounded-lg p-3">
                <p className="text-xs text-gray-400">Max. Strom ({result.recommended}mm2)</p>
                <p className="text-lg font-bold text-white">{result.maxCurrent} A</p>
              </div>
            </div>

            <div className="text-xs text-gray-500 mt-2 p-2 bg-[#1a1f2e] rounded">
              Hinweis: Werte basieren auf Standardverlegeart. Fuer genaue Auslegung DIN VDE 0298-4 beachten.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
